/* eslint-disable react-refresh/only-export-components */
import {
  CustomSorterFunction,
  Orama,
  Result,
  Results,
  SearchParams,
  SearchableType,
  WhereCondition,
  create,
  getByID,
  insertMultiple,
  search,
  updateMultiple,
} from '@orama/orama';
import {
  Article,
  CategorieWeb,
  HitCategorieWeb,
  HitItemCatalogue,
  HitItemMarketplace,
  NatureDuPrix,
  PaysEnum,
  SearchClient,
} from 'api-types/ffconnect';
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {deburr, intersection, uniq} from 'lodash-es';
import {Navigable, State} from '@synako/halitrek';
import {ClientDesc, ItemCatalogue2Desc} from 'api-types/ffconnect-hal';
import {useHalState, useHalStates} from '@synako/haligator';

export interface FiltresItemCatalogueConfig {
  Data: FiltresItemCatalogue;
  Coerce: typeof coerceFiltresItemCatalogue;
}

/**
 * Au final le meilleur calcul du score permet de déclencher sur des tokens plus petits types : OS, VF, etc.
 */
export const SEARCH_MIN_CHARS = 2;
const SEARCH_STOP_WORDS = new Set(['de', 'du', 'des']);

/**
 * Permet de faire jouer "plus" le tri standard mais réduit la pertinence de la correspondance textuelle
 */
export const ECART_SCORE_TRI = 3;

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type FiltresItemCatalogue = {
  /** Recherche full-text sur le libelle_commercial * 4 + mot_directeur */
  q?: string;
  est_cadencier?: boolean;
  cat_web?: string[];
  nature_du_prix?: NatureDuPrix;
  produit_marche?: boolean; // Filtre pour les produits marché
  est_ancien_devis?: boolean; // Filtre pour les produits des anciens devis

  // Pas utilisé directement dans les filtres, juste en remplacement
  mot_directeur?: string;
  // Sémantique ANY exacte sur le mot directeur entier (side panel UB CREMIERS).
  // Filtré via `mot_directeur_exact` (enum), pas via le string tokenisé.
  mot_directeur_any?: string[];
  cat_std?: string[];
  // Sémantique ANY (intersection) sur `cat_std` — un article matche s'il appartient
  // à AU MOINS une des catégories. Utilisé par le side panel des UB Essentielles
  // (une UB regroupe plusieurs sous-sous-catégories). Distinct de `cat_std`
  // (containsAll). Mutuellement exclusif : si fourni, prime sur `cat_std`.
  cat_std_any?: string[];

  // Utilisé uniquement pour le classement
  // est_TAC?: boolean;
  est_MP?: boolean;
  // type_de_marque?: string[],
  // delai_dispo?: number

  pays_d_origine?: PaysEnum[];
  labels?: string[];
  sortBy?: 'pertinence' | 'alphabetique';
  page?: number;
  precommande?: boolean;
  temperature_livraison?: 'positif' | 'negatif';
  afficher_marketplace?: boolean;
  marketplace_id?: string;
  cat_marketplace?: string[];
  // Caractéristiques fromage
  famille_fromage?: string[];
  type_de_lait?: string[];
  traitement_thermique_du_lait?: string[];
};

export type FiltresItemCatalogueGrandCompte = FiltresItemCatalogue & {
  est_TAC?: boolean;
  est_MP?: boolean;
  nature_du_prix?: string;
  labels?: string[];
  temperature_livraison?: 'positif' | 'negatif';
};

export const coerceFiltresItemCatalogue = {
  q: 'string',
  nature_du_prix: 'string',
  mot_directeur: 'string',
  mot_directeur_any: 'string[]',
  cat_web: 'string[]',
  cat_std: 'string[]',
  cat_std_any: 'string[]',
  est_cadencier: 'boolean',
  pays_d_origine: 'string[]',
  labels: 'string[]',
  sortBy: 'string',
  est_MP: 'boolean',
  // type_de_marque: 'string[]',
  // type_de_mar
  precommande: 'boolean',
  temperature_livraison: 'string',
  produit_marche: 'boolean',
  est_ancien_devis: 'boolean',
  afficher_marketplace: 'boolean',
  marketplace_id: 'string',
  cat_marketplace: 'string[]',
  // Caractéristiques fromage
  famille_fromage: 'string[]',
  type_de_lait: 'string[]',
  traitement_thermique_du_lait: 'string[]',

  page: 'number', // Uniquement pour le back
} as const;

export const coerceFiltresItemCatalogueGrandCompte = {
  ...coerceFiltresItemCatalogue,
  est_TAC: 'boolean',
  est_MP: 'boolean',
  nature_du_prix: 'string',
  labels: 'string[]',
  temperature_livraison: 'string',
} as const;

export const ITEMS_PAR_PAGE = 30;

// Coquille commune : articles FF, filtre précommande, tri comme la recherche
// catalogue sans texte (`triStandard` : cadencier, stock, TAC, MP, MDD, puis
// libellé). Le critère métier (catégorie / mot directeur) est passé en `matche`.
function filtrerEtTrierArticlesFF(
  docs: HitItemCatalogue[],
  {enleverPrecommande}: {enleverPrecommande: boolean},
  matche: (hit: HitItemCatalogue) => boolean,
): HitItemCatalogue[] {
  const selection = docs.filter((hit) => {
    if (hit.marketplace_id !== 'FF') return false;
    if (hit.precommande && enleverPrecommande) return false;
    return matche(hit);
  });
  selection.sort((a, b) => triStandard([0, 0, a], [0, 0, b]));
  return selection;
}

function toOramaResults(
  selection: HitItemCatalogue[],
): Results<HitItemCatalogue> {
  const hits: Result<HitItemCatalogue>[] = selection.map((h) => ({
    document: h,
    id: h.code,
    score: 0,
  }));
  return {
    count: hits.length,
    // Filtrage JS (pas d'appel Orama) : elapsed factice pour coller au type Results.
    elapsed: {raw: 0, formatted: '0ms'},
    hits,
  };
}

// Sélectionne et trie les articles FF appartenant à au moins une des
// sous-sous-catégories standard passées.
export function selectionnerArticlesPourCategoriesUB(
  docs: HitItemCatalogue[],
  categories: string[],
  opts: {enleverPrecommande: boolean},
): HitItemCatalogue[] {
  return filtrerEtTrierArticlesFF(docs, opts, (hit) =>
    categories.some((cat) => hit.cat_std.includes(cat)),
  );
}

// Ensemble des sous-sous-catégories standard (`cat_std`) présentes parmi les
// articles FF passés.
export function collecterCategoriesStdFF(
  docs: HitItemCatalogue[],
): Set<string> {
  const set = new Set<string>();
  for (const hit of docs) {
    if (hit.marketplace_id !== 'FF') continue;
    for (const cat of hit.cat_std) {
      set.add(cat);
    }
  }
  return set;
}

// Filtre Orama ANY sur le mot directeur entier. Le champ `string` tokenise :
// « COMTE 12 MOIS COUPE » matcherait aussi « EMMENTAL COUPE » via « COUPE ».
export function filtreMotDirecteurExact(motsDirecteurs: string[]): {
  mot_directeur_exact: {in: string[]};
} {
  return {mot_directeur_exact: {in: motsDirecteurs}};
}

// Sélectionne et trie les articles FF dont le mot_directeur est dans la liste
// (sémantique ANY, valeur entière).
export function selectionnerArticlesPourMotsDirecteurs(
  docs: HitItemCatalogue[],
  motsDirecteurs: string[],
  opts: {enleverPrecommande: boolean},
): HitItemCatalogue[] {
  const set = new Set(motsDirecteurs);
  return filtrerEtTrierArticlesFF(
    docs,
    opts,
    (hit) => !!hit.mot_directeur && set.has(hit.mot_directeur),
  );
}

// Ensemble des mots directeurs présents parmi les articles FF passés.
export function collecterMotsDirecteursFF(
  docs: HitItemCatalogue[],
): Set<string> {
  const set = new Set<string>();
  for (const hit of docs) {
    if (hit.marketplace_id !== 'FF') continue;
    if (hit.mot_directeur) set.add(hit.mot_directeur);
  }
  return set;
}

export function useSearchClient() {
  const indexes = useContext(SearchClientContext);
  const {error, isBuilding, marketplaceReady, indexVersion} =
    useSearchClientProviderState();

  const getNomenclatureWeb = () => {
    if (!indexes?.categories_idx.data.docs.docs) {
      return undefined;
    }
    const docs = indexes.categories_idx.data.docs.docs as Record<
      string,
      CategorieWeb
    >;
    return Object.values(docs);
  };

  return {
    indexIsBuilt: !!indexes,
    indexError: error,
    isBuilding,
    marketplaceReady,
    indexVersion,
    indexes,
    societeCommercialeId: indexes?.societe_commerciale_id,
    // Items du catague
    searchItems,
    remplacementPour,
    remplacementPour2,
    articlesPourCategoriesUB,
    categoriesStdAvecProduits,
    articlesPourMotsDirecteursUB,
    motsDirecteursAvecProduits,
    // Catégories Web
    categorieCounts,
    searchCategories,
    tokenize,
    getNomenclatureWeb,
  };

  async function searchItems(
    filters: FiltresItemCatalogue,
    itemsPerPage = ITEMS_PAR_PAGE,
  ) {
    const where: Partial<WhereCondition<CatalogueOramaSchema>> = {
      ...(filters.cat_std &&
        filters.cat_std.length > 0 && {
          cat_std: {containsAll: filters.cat_std},
        }),
      // Placé après `cat_std` : si `cat_std_any` est fourni, il prime (même clé Orama).
      ...(filters.cat_std_any &&
        filters.cat_std_any.length > 0 && {
          cat_std: {containsAny: filters.cat_std_any},
        }),
      ...(filters.cat_web &&
        filters.cat_web.length > 0 && {
          cat_web: {containsAll: filters.cat_web},
        }),
      ...(filters.nature_du_prix && {
        nature_du_prix: {eq: filters.nature_du_prix},
      }),
      ...(filters.mot_directeur && {
        mot_directeur: filters.mot_directeur,
      }),
      ...(filters.mot_directeur_any &&
        filters.mot_directeur_any.length > 0 &&
        filtreMotDirecteurExact(filters.mot_directeur_any)),
      ...(filters.pays_d_origine &&
        filters.pays_d_origine.length > 0 && {
          pays_d_origine: {containsAll: filters.pays_d_origine},
        }),
      ...(filters.labels &&
        filters.labels.length > 0 && {
          labels: {containsAll: filters.labels},
        }),
      ...(filters.precommande !== undefined && {
        precommande: filters.precommande,
      }),
      ...(filters.est_MP && {
        type_de_marque: {eq: 'MP'},
      }),
      ...(filters.temperature_livraison && {
        temperature_livraison: {eq: filters.temperature_livraison},
      }),
      ...(filters.est_cadencier !== undefined && {
        est_cadencier: filters.est_cadencier,
      }),
      ...(filters.produit_marche !== undefined && {
        produit_marche: filters.produit_marche,
      }),
      ...(filters.est_ancien_devis !== undefined && {
        est_ancien_devis: filters.est_ancien_devis,
      }),
      ...(filters.afficher_marketplace === false && {
        marketplace_id: {eq: 'FF'},
      }),
      ...(filters.marketplace_id && {
        marketplace_id: {eq: filters.marketplace_id},
      }),
      ...(filters.cat_marketplace &&
        filters.cat_marketplace.length > 0 && {
          cat_marketplace: {containsAll: filters.cat_marketplace},
        }),
      // Caractéristiques fromage
      ...(filters.famille_fromage &&
        filters.famille_fromage.length > 0 && {
          famille_fromage: {in: filters.famille_fromage},
        }),
      ...(filters.type_de_lait &&
        filters.type_de_lait.length > 0 && {
          type_de_lait: {in: filters.type_de_lait},
        }),
      ...(filters.traitement_thermique_du_lait &&
        filters.traitement_thermique_du_lait.length > 0 && {
          traitement_thermique_du_lait: {
            in: filters.traitement_thermique_du_lait,
          },
        }),
    };

    const page = filters.page ?? 1;
    const offset = (page - 1) * itemsPerPage;
    // Facettes partagées avec la passe « code exact » plus bas :
    // une facette absente fait disparaître le filtre correspondant
    // de la barre de filtres, ou plante son lecteur.
    const facets: CatalogueSearchParams['facets'] = {
      cat_web: {
        limit: 100,
      },
      cat_std: {},
      cat_marketplace: {
        limit: 100,
      },
      pays_d_origine: {},
      labels: {},
      nature_du_prix: {},
      temperature_livraison: {},
      produit_marche: {},
      marketplace_id: {},
      famille_fromage: {},
      type_de_lait: {},
      traitement_thermique_du_lait: {},
    };
    const params: CatalogueSearchParams = {
      where,
      limit: itemsPerPage,
      offset,
      facets,
    };

    if (filters.sortBy === 'alphabetique') {
      params.sortBy = triAlphabetique;
    } else {
      params.sortBy = triStandard;
    }

    if (filters.q) {
      params.term = filters.q;
      params.properties = [
        'code',
        'libelle_commercial',
        'marque',
        'mot_directeur',
        'code_article_client',
        'code_generique',
      ];
      // exact: true,
      params.tolerance = 1; // c'est sur qu'on veut cela, c'est que qui donne la typo tolerance
      // si on met 2 en tolérance ça ramène plus mais aussi plus de bruit
      params.threshold = 1; // Recherche plus large et surtout permet de récupérer les facets si recherche sans terme

      params.sortBy = triRecherche(filters.q);

      // Le boost n'est pas vraiment utile si dernière on ne corrige pas avec le match exact
      // Exemple: Maitres laitiers creme fait que le lait fait un gros score alors que c'est juste la similarité avec "laitiers"
      params.boost = {
        libelle_commercial: 2,
        mot_directeur: 1,
        marque: 0.5,
        code: 1.5,
        code_article_client: 1.5, // Boost added
        code_generique: 1.5, // Boost added
      };
    }
    // FUTURE: à voir si on combine 2 ou 3 recherches en jouant sur exact et threshold ?

    // Si la requête ressemble à un seul token de code, on tente d'abord une recherche stricte sur le champ 'code'.
    // Objectif: faire fonctionner les recherches de codes exacts (ex: ST72592) même si la recherche full-text échoue.
    if (filters.q) {
      const codeLikeTokens = customTokenizer.tokenize(filters.q);
      if (codeLikeTokens.length === 1) {
        const prepassParams: CatalogueSearchParams = {
          term: filters.q,
          properties: ['code'],
          tolerance: 0,
          threshold: 0,
          where,
          limit: itemsPerPage,
          offset,
          facets,
        };
        const prepass = await search(indexes!.catalogue_idx, prepassParams);
        if (prepass.hits.length > 0) {
          // On retourne directement les résultats exacts pour un code.
          return prepass;
        }
      }
    }

    const results = await search(indexes!.catalogue_idx, params);

    return results;
  }

  function tokenize(s: string) {
    return indexes!.catalogue_idx.tokenizer.tokenize(s);
  }

  async function remplacementPour(
    art: Article,
    filters: {
      // nature_du_prix?: NatureDuPrix,
      // pays_d_origine?: PaysEnum[],
      // labels?: string[],
      enleverPrecommande: boolean;
    },
  ) {
    const artT = art.tolede;
    return remplacementPour2({
      code_article: artT.code_article,
      sous_sous_famille_std: `${artT.code_activite}-${artT.code_famille}-${artT.code_sous_famille}-${artT.code_sous_sous_famille}`,
      mot_directeur: artT.mot_directeur,
      // q,
      ...filters,
    });
  }

  async function remplacementPour2(config: {
    code_article: string;
    // Cela permet d'une part de ramasser d'autre part de classer
    sous_sous_famille_std: string;
    // Cela permet de ramasser
    mot_directeur: string | undefined;
    enleverPrecommande: boolean;
  }) {
    const {
      code_article,
      sous_sous_famille_std,
      mot_directeur,
      enleverPrecommande,
    } = config;
    // Sélection de tous ceux qui ont le même mot directeur
    // Sélection de la même sous_sous_famille
    const docs: HitItemCatalogue[] = Object.values(
      indexes!.catalogue_idx.data.docs.docs,
    );
    const selection = docs.filter((hit) => {
      if (hit.marketplace_id !== 'FF') return false;
      if (hit.precommande && enleverPrecommande) return false;
      if (hit.code === code_article) return false;
      return (
        hit.cat_std.includes(sous_sous_famille_std) ||
        hit.mot_directeur === mot_directeur
      );
    });

    const hits: Result<HitItemCatalogue>[] = selection.map((h) => ({
      document: h,
      id: h.code,
      score: 0,
    }));
    const sortFn = triRemplacement(mot_directeur, sous_sous_famille_std);
    hits.sort((A, B) =>
      sortFn([0, A.score, A.document], [0, B.score, B.document]),
    );
    // Maintenant il faut trier :
    // - d'abord ce qui n'est pas précommande (d'ailleurs dans certain cas il faut exclure).
    // - d'abord le même mot directeur puis même sous_sous_famille
    // - par contre il faut mettre aussi toutes

    // TODO: hits.sort();

    const final: Results<HitItemCatalogue> = {
      count: hits.length,
      elapsed: {
        raw: 1,
        formatted: 'TODO',
      },
      hits,
    };
    return final;
  }

  // Tous les articles du catalogue FF appartenant à l'une des sous-sous-catégories
  // standard passées. Filtrage JS sur l'index en mémoire.
  async function articlesPourCategoriesUB(
    categories: string[],
    {enleverPrecommande}: {enleverPrecommande: boolean},
  ): Promise<Results<HitItemCatalogue>> {
    const docs: HitItemCatalogue[] = Object.values(
      indexes!.catalogue_idx.data.docs.docs,
    );
    return toOramaResults(
      selectionnerArticlesPourCategoriesUB(docs, categories, {
        enleverPrecommande,
      }),
    );
  }

  // Ensemble des sous-sous-catégories standard (`cat_std`) effectivement présentes
  // au catalogue FF du client. Un seul scan de l'index (indépendant du nombre d'UB).
  function categoriesStdAvecProduits(): Set<string> {
    if (!indexes) return new Set<string>();
    const docs: HitItemCatalogue[] = Object.values(
      indexes.catalogue_idx.data.docs.docs,
    );
    return collecterCategoriesStdFF(docs);
  }

  // Tous les articles FF dont le mot_directeur est dans la liste (CREMIERS).
  async function articlesPourMotsDirecteursUB(
    motsDirecteurs: string[],
    {enleverPrecommande}: {enleverPrecommande: boolean},
  ): Promise<Results<HitItemCatalogue>> {
    const docs: HitItemCatalogue[] = Object.values(
      indexes!.catalogue_idx.data.docs.docs,
    );
    return toOramaResults(
      selectionnerArticlesPourMotsDirecteurs(docs, motsDirecteurs, {
        enleverPrecommande,
      }),
    );
  }

  // Ensemble des mots directeurs présents au catalogue FF du client.
  function motsDirecteursAvecProduits(): Set<string> {
    if (!indexes) return new Set<string>();
    const docs: HitItemCatalogue[] = Object.values(
      indexes.catalogue_idx.data.docs.docs,
    );
    return collecterMotsDirecteursFF(docs);
  }

  async function categorieCounts(filters?: FiltresItemCatalogue) {
    const where: WhereCondition<CatalogueOramaSchema> = {
      ...(filters?.nature_du_prix && {
        nature_du_prix: {eq: filters.nature_du_prix},
      }),
    };

    const query: CatalogueSearchParams = {
      where,
      facets: {
        cat_web: {
          limit: 100,
        },
      },
    };
    const r = await search(indexes!.catalogue_idx, query);
    return {
      totalCount: r.count,
      elapsed: r.elapsed,
      ...r.facets!['cat_web'],
    };
  }

  async function searchCategories({q}: {q: string}) {
    const params: CategorieSearchParams = {
      term: q,
      properties: ['libelle'],
      tolerance: 1, // c'est sur qu'on veut cela, c'est que qui donne la typo tolerance
      threshold: 1, // Recherche plus large et surtout permet de récupérer les facets si recherche sans terme
      limit: 5,
    };
    return search(indexes!.categories_idx, params);
  }
}

export type ExtendendSortItem = [
  number,
  number,
  HitItemCatalogue,
  number | undefined,
];

// Exemple 1 : boeuf a decouper
// Exemple 2 : creme maitres laitiers
// Cla veut dire qu'il faut redresser les scores
export const triRecherche = (
  q: string,
): CustomSorterFunction<HitItemCatalogue> => {
  const qTokens = customTokenizer.tokenize(q);
  function boostExact(hit: HitItemCatalogue, _scoreOriginal: number) {
    const hitTokens = customTokenizer.tokenize(hit.libelle_commercial);
    const exactLibTokens = intersection(qTokens, hitTokens);
    const prefixLibTokens = hitTokens.filter((hToken) => {
      return (
        !!qTokens.find((qToken) => hToken.startsWith(qToken)) &&
        exactLibTokens.includes(hToken)
      );
    });

    let exactMarqueTokens: string[] = [];
    if (hit.marque) {
      exactMarqueTokens = intersection(
        qTokens,
        customTokenizer.tokenize(hit.marque),
      );
    }

    let exactMotDirecteurTokens: string[] = [];
    if (hit.mot_directeur) {
      exactMotDirecteurTokens = intersection(
        qTokens,
        customTokenizer.tokenize(hit.mot_directeur),
      );
    }

    // Certains codes ne sont pas matchés correctement si on les met pas en minuscule explicitement
    const codeExact = intersection(qTokens, [hit.code.toLowerCase()]);
    const codeClientExact = hit.code_article_client
      ? intersection(qTokens, [hit.code_article_client.toLowerCase()])
      : [];
    const codeGeneriqueExact = hit.code_generique
      ? intersection(qTokens, [hit.code_generique.toLowerCase()])
      : [];

    // if(['170981', '104333', '181225', '105465'].includes(hit.code)) {
    //   console.log({hit, scoreOriginal, qTokens, hitTokens, exactLibTokens, exactMarqueTokens});
    // }

    return (
      exactLibTokens.length * 10 +
      prefixLibTokens.length * 6 +
      (qTokens[0] === hitTokens[0] ? 10 : 0) +
      codeExact.length * 20 + // Match exact du code !
      exactMotDirecteurTokens.length * 3 +
      exactMarqueTokens.length * 2 +
      codeClientExact.length * 20 +
      codeGeneriqueExact.length * 20
    );
  }
  return (A, B) => {
    // Modification du score d'origine.
    const [, scoreA, docA, origScoreA] = A as unknown as ExtendendSortItem;
    const [, scoreB, docB, origScoreB] = B as unknown as ExtendendSortItem;
    // Calcul des scores redressés
    if (!origScoreA) {
      A[1] = scoreA / 2 + boostExact(docA, scoreA);
      // A[1] = boostExact(docA);
      A.push(scoreA);
    }
    if (!origScoreB) {
      B[1] = scoreB / 2 + boostExact(docB, scoreB);
      // B[1] = boostExact(docB);
      B.push(scoreB);
    }

    const diff = B[1] - A[1];
    if (Math.abs(diff) < ECART_SCORE_TRI) {
      return triStandard(A, B);
    }
    return diff;
  };
};

const triAlphabetique: CustomSorterFunction<HitItemCatalogue> = (
  [, , itemA],
  [, , itemB],
) => {
  return itemA.libelle_commercial.localeCompare(itemB.libelle_commercial);
};

const triRemplacement = (
  mot_directeur: string | undefined,
  sous_sous_famille_std: string,
): CustomSorterFunction<HitItemCatalogue> => {
  return createTri([
    function est_Stock(hit) {
      return !hit.precommande;
    },
    function memeMotDirecteur(hit) {
      return mot_directeur ? hit.mot_directeur === mot_directeur : false;
    },
    function memeSSF(hit) {
      return hit.cat_std.includes(sous_sous_famille_std);
    },
    'est_cadencier',
    'est_TAC',
    function est_MP(hit) {
      return hit.type_de_marque === 'MP';
    },
    function est_MDD(hit) {
      return hit.type_de_marque === 'MDD';
    },
  ]);
};

export const triStandard = createTri([
  'est_cadencier',
  function est_Stock(hit) {
    return !hit.precommande;
  },
  'est_TAC',
  function est_MP(hit) {
    return hit.type_de_marque === 'MP';
  },
  function est_MDD(hit) {
    return hit.type_de_marque === 'MDD';
  },
]);

type PrioriteDeTri = (
  | keyof HitItemCatalogue
  | ((hit: HitItemCatalogue) => boolean)
)[];
function createTri(
  prios: PrioriteDeTri,
): CustomSorterFunction<HitItemCatalogue> {
  return (A, B) => {
    const [, scoreA, itemA] = A;
    const [, scoreB, itemB] = B;
    for (const test of prios) {
      let valA;
      let valB;
      if (typeof test === 'function') {
        valA = test(itemA);
        valB = test(itemB);
      } else {
        valA = itemA[test];
        valB = itemB[test];
      }
      // S'ils sont différents alors le vrai prend la priorité
      if (valA !== valB) {
        return valA ? -1 : 1;
      }
    }
    if (scoreA === scoreB) {
      return triAlphabetique(A, B);
    }
    return scoreB - scoreA;
  };
}

export interface SearchIndexes {
  catalogue_idx: Orama<CatalogueOramaSchema>;
  categories_idx: Orama<CategorieOramaSchema>;
  societe_commerciale_id?: string;
}

// Define Orama schema based on the fields intended for indexing from HitItemCatalogue
// Exclude fields like vignette_url and priorite_generique which are not indexed/searched directly
type IndexedKeys = keyof Omit<HitItemCatalogue, 'delai_dispo' | 'vignette_url'>;
type CatalogueOramaSchema = Record<IndexedKeys, SearchableType> & {
  // Copie non tokenisée de `mot_directeur` : filtre ANY exact du side panel CREMIERS.
  mot_directeur_exact: 'enum';
};

type CategorieOramaSchema = Record<
  keyof Omit<HitCategorieWeb, 'id'>,
  SearchableType
>;

type CatalogueOrama = Orama<CatalogueOramaSchema>;
type CategorieOrama = Orama<CategorieOramaSchema>;

type CatalogueSearchParams = SearchParams<CatalogueOrama, HitItemCatalogue>;
type CategorieSearchParams = SearchParams<CategorieOrama, HitCategorieWeb>;

const remplacements: Record<string, string[]> = {
  '1/2': ['demi'],
  st: ['saint'],
  ent: ['entier'],
  entieres: ['entier'],
  lc: ['lait', 'cru'],
  bte: ['boite'],
  vanillee: ['vanille'],
  vanil: ['vanille'],
  croissants: ['croissant'],
  couv: ['couverture'],
  // Spéciale Sud-Ouest
  chocolatine: ['pain', 'chocolat'],
  // On tape Noix mais il faut que ça match en priorité les cerneaux de noix
  cerneaux: ['noix', 'cerneaux'],
  cerneau: ['noix', 'cerneau'],
  coule: ['oeuf', 'liquide', 'entier'],
  coul: ['oeuf', 'liquide', 'entier'],

  //#1808
  matje: ['hareng'],
  matjes: ['hareng'],
  chicon: ['endive'],
  parmigiano: ['parmesan'],
  parmiggiano: ['parmesan'],
  parmesan: ['parmigiano', 'parmiggiano'],

  //#2448
  'a/p': ['avec', 'peau'],
  's/p': ['sans', 'peau'],

  // Faisselle <-> fromage blanc
  faisselle: ['fromage', 'blanc'],
  'fromage blanc': ['faisselle'],

  //#3313 Ballotine <-> Galantine
  ballotine: ['galantine'],
  galantine: ['ballotine'],
};

export const customTokenizer /* : Tokenizer */ = {
  tokenize: (raw: string, _language?: string, prop?: string): string[] => {
    if (
      prop === 'code' ||
      prop === 'code_article_client' ||
      prop === 'code_generique'
    ) {
      return raw ? [raw.toLowerCase().trim()] : [];
    }
    const split = deburr(raw.toLocaleLowerCase())
      .replace(/[\s\n\r]/g, ' ')
      .trim()
      .split(' ');

    // Gérer apostrophes
    const withApostrophes = split.flatMap((word) => {
      if (word.includes("'")) {
        const parts = word.split("'");
        // Ajouter à la fois le mot original et les parties après apostrophes
        // Filtrer les parties vides et s'assurer qu'elles respectent la longueur minimum
        return [word, ...parts.filter((p) => p.length >= SEARCH_MIN_CHARS)];
      }
      return [word];
    });

    const rempl = withApostrophes.flatMap((s: string) => [
      s,
      ...(remplacements[s] ?? []),
    ]);
    // return rempl;
    // const filtered = rempl.filter(p => p.length > 2 || parseInt(p, 10) > 10);
    const filtered = rempl.filter(
      (p: string) => p.length >= SEARCH_MIN_CHARS && !SEARCH_STOP_WORDS.has(p),
    );
    // évite que la "crème de la crème soit toujours en premier"
    const uniqued = uniq(filtered);
    return uniqued;
  },
  language: 'fr',
  normalizationCache: new Map(),
};

// Mappe un HitItemMarketplace vers HitItemCatalogue pour insertion dans l'index Orama
export function mapMarketplaceToHitItemCatalogue(
  item: HitItemMarketplace,
): HitItemCatalogue {
  return {
    code: item.ref_tiers,
    libelle_commercial: item.libelle,
    marketplace_id: item.marketplace_id,
    ref_tiers: item.ref_tiers,
    marque: item.marque,
    vignette_url: item.vignette_url,
    // Valeurs par défaut pour les champs catalogue-only
    cat_marketplace: item.cat_marketplace ?? [],
    cat_std: [],
    cat_web: [],
    precommande: false,
    temperature_livraison: 'positif',
    produit_marche: false,
    est_TAC: false,
    est_cadencier: false,
    est_ancien_devis: false,
    pays_d_origine: [],
    labels: [],
  };
}

export async function buildIndexes(
  search_client: SearchClient,
  cadencierStates: State<ItemCatalogue2Desc>[] | undefined,
): Promise<SearchIndexes> {
  const schemaCatalogue: CatalogueOramaSchema = {
    code: 'string',
    code_article_client: 'string',
    code_generique: 'string',
    libelle_commercial: 'string',
    mot_directeur: 'string',
    mot_directeur_exact: 'enum',
    marque: 'string',
    cat_web: 'enum[]',
    cat_std: 'enum[]',
    est_TAC: 'boolean',
    type_de_marque: 'enum',
    est_cadencier: 'boolean',
    nature_du_prix: 'enum',
    pays_d_origine: 'enum[]',
    labels: 'enum[]',
    multiple_de_commande_client: 'number',
    precommande: 'boolean',
    temperature_livraison: 'enum',
    priorite_generique: 'number',
    produit_marche: 'boolean',
    est_ancien_devis: 'boolean',
    marketplace_id: 'enum',
    ref_tiers: 'string',
    cat_marketplace: 'enum[]',
    famille_fromage: 'enum',
    type_de_lait: 'enum',
    traitement_thermique_du_lait: 'enum',
  };
  const catalogue_idx = create({
    id: 'catalogue',
    schema: schemaCatalogue,
    components: {
      // tokenizer: {
      //   // language: 'french',
      //   tokenizeSkipProperties: ['code', 'cat_web'],
      //   stemming: false,
      //   stemmerSkipProperties: ['code', 'cat_web'],
      //   stopWords
      // },
      tokenizer: customTokenizer,
    },
  });

  const schemaCategorie: CategorieOramaSchema = {
    // id: undefined, // Non-indexé
    libelle: 'string',
    id_parent: 'string',
  };
  const categories_idx = create({
    id: 'categories',
    schema: schemaCategorie,
    components: {
      // tokenizer: {
      //   tokenizeSkipProperties: ['id'],
      //   stemming: false,
      //   stemmerSkipProperties: ['id'],
      //   stopWords
      // },
      tokenizer: customTokenizer,
    },
  });

  const cadencierCodeSet = new Set(
    cadencierStates?.map((state) => state.data.code) ?? [],
  );

  // On prépare les données du catalogue en tenant compte du cadencier
  const items_catalogue = search_client.items_catalogue.map((item) => {
    // On vérifie si l'article est dans le cadencier using the Set
    const isInCadencier = cadencierCodeSet.has(item.code);
    const processedItem = {
      // id = code : Orama utilise doc.id comme external id (cf. getDocumentIndexId).
      // Sans ça, update() ne peut pas retrouver le doc par son code pour le patcher.
      id: item.code,
      // Keep code_generique only if priorite_generique was valid
      ...item,
      code_generique:
        item.code_generique && typeof item.priorite_generique === 'number'
          ? item.code_generique
          : undefined,
      // Update est_cadencier
      est_cadencier: isInCadencier,
      produit_marche: item.produit_marche,
      // Le schéma SearchClient exige marketplace_id, Orama reçoit donc toujours une valeur.
      marketplace_id: item.marketplace_id,
      cat_marketplace: item.cat_marketplace ?? [],
      // Absent si l'article n'a pas de mot directeur : l'enum ignore `undefined`.
      mot_directeur_exact: item.mot_directeur,
    };

    // Supprime code_generique si undefined
    if (processedItem.code_generique === undefined) {
      delete processedItem.code_generique;
    }

    return processedItem;
  });

  // Insertion par lots pour éviter de bloquer le thread principal
  const BATCH_SIZE = 1000;

  // Insertion des items du catalogue par lots
  for (let i = 0; i < items_catalogue.length; i += BATCH_SIZE) {
    const batch = items_catalogue.slice(i, i + BATCH_SIZE);
    await insertMultiple(
      catalogue_idx,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      batch as any,
      undefined,
      undefined,
      true,
    );
  }

  await insertMultiple(
    categories_idx,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    search_client.categories as any,
    undefined,
    undefined,
    true,
  );

  return {
    catalogue_idx,
    categories_idx,
  };
}

export const SearchClientContext = createContext<SearchIndexes | undefined>(
  undefined,
);

// Create a new context to expose error state
export interface SearchClientProviderState {
  error: Error | null;
  isBuilding: boolean;
  marketplaceReady: boolean;
  // Incrémenté à chaque patch ciblé de l'index (ex: est_cadencier),
  // permet aux consommateurs de re-requêter sans rebuild complet
  indexVersion: number;
}

export const SearchClientProviderStateContext =
  createContext<SearchClientProviderState>({
    error: null,
    isBuilding: false,
    marketplaceReady: false,
    indexVersion: 0,
  });

export function SearchClientProvider({
  client,
  children,
  searchQuery,
}: PropsWithChildren<{
  client: Navigable<ClientDesc>;
  // Exemple d'utilisation de searchQuery:
  // Passer l'id du devis en cours à get-search_client afin d'exclure les produits du devis actuel
  // des produits des autres devis (masqués ou visibles) pour l'onglet 'Devis personnalisés'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchQuery?: Record<string, any>;
}>) {
  const [indexes, setIndexes] = useState<SearchIndexes>();
  const [error, setError] = useState<Error | null>(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [marketplaceReady, setMarketplaceReady] = useState(false);
  const [indexVersion, setIndexVersion] = useState(0);

  const cadencierNav = useMemo(() => client.follow('cadencier'), [client]);
  const cadencierStates = useHalStates(
    cadencierNav.followAll('items_catalogue_2'),
  ).states;

  const searchNav = client.follow('search', searchQuery);
  const searchState = useHalState(searchNav);
  const searchData = searchState.data;

  // Le lien search_marketplace est porté par la collection marketplaces et n'est présent
  // que si le client a accès à Matfer.
  const catalogueMarketplaceNav = client
    .follow('marketplaces')
    .follow('search_marketplace');
  const catalogueMarketplaceState = useHalState(catalogueMarketplaceNav);
  const catalogueMarketplaceData = catalogueMarketplaceState.data;

  const hasBuiltIndexes = useRef(false);
  const hasInsertedMarketplace = useRef(false);

  // Patch ciblé via update() : on diff le cadencier et on met à jour les seuls docs
  // concernés — évite un rebuild complet (coûteux + perdrait les items marketplace).
  const cadencierCodesKey = useMemo(() => {
    if (!cadencierStates) return null;
    // La clé est une concat des codes présents dans le cadencier, triés pour éviter les faux changements de clé si l'ordre change mais pas les codes.
    return cadencierStates
      .map((s) => s.data.code)
      .sort()
      .join(',');
  }, [cadencierStates]);

  // Codes du cadencier lors du dernier build/patch, pour calculer le diff
  const lastCadencierCodes = useRef<Set<string> | null>(null);

  useEffect(() => {
    // On attend que le cadencier soit chargé et que l'index Orama soit construit avant de patcher.
    if (!cadencierStates || !indexes) return;

    // Codes articles actuellement présents dans le cadencier (source de vérité HAL).
    const newCodes = new Set(cadencierStates.map((s) => s.data.code));

    // Enregistrer les codes lors du premier build de l'index.
    if (lastCadencierCodes.current === null) {
      lastCadencierCodes.current = newCodes;
      return;
    }

    // Calculer le diff : articles ajoutés / supprimés du cadencier
    const prevCodes = lastCadencierCodes.current; // Set des codes lors du dernier patch
    const added = [...newCodes].filter((c) => !prevCodes.has(c)); // Nouveaux codes à ajouter
    const removed = [...prevCodes].filter((c) => !newCodes.has(c)); // Codes à retirer

    if (added.length === 0 && removed.length === 0) return;

    // Set des codes ajoutés pour un lookup O(1) lors de la détermination de est_cadencier.
    const addedSet = new Set(added);
    // Liste unifiée des codes à patcher (ajoutés + supprimés), pour une seule passe.
    const changedCodes = [...added, ...removed];
    // Accumulateurs pour l'appel batch à updateMultiple (ids et docs alignés par index).
    const idsToUpdate: string[] = [];
    const docsToUpdate: HitItemCatalogue[] = [];

    // getByID permet d'éviter de scanner tout l'index à chaque modification.
    for (const code of changedCodes) {
      const doc = getByID(indexes.catalogue_idx, code) as
        | HitItemCatalogue
        | undefined;
      if (!doc) continue;

      // On prépare le patch du document avec le nouveau flag est_cadencier selon qu'il a été ajouté ou retiré du cadencier.
      idsToUpdate.push(code); // id du doc à patcher
      docsToUpdate.push({
        ...doc, // garde tous les champs existants
        est_cadencier: addedSet.has(code), // sauf celui-ci
      });
    }

    if (idsToUpdate.length === 0) return;

    try {
      // Patch batch de l'index Orama pour mettre à jour le flag est_cadencier des articles concernés par le diff.
      updateMultiple(
        indexes.catalogue_idx,
        idsToUpdate,
        docsToUpdate,
        undefined,
        undefined,
        true,
      );
      // On ne mémorise les nouveaux codes qu'après un update réussi, pour que le prochain diff
      // reparte du dernier état cohérent avec l'index en cas d'échec.
      lastCadencierCodes.current = newCodes;

      // Notifier les consommateurs (MonCadencier) pour qu'ils re-requêtent l'index
      setIndexVersion((v) => v + 1);
    } catch (err: unknown) {
      console.error(
        'Failed to patch est_cadencier flags in search index:',
        err,
      );
    }
  }, [cadencierCodesKey, cadencierStates, indexes]);

  useEffect(() => {
    if (searchData === undefined) return;
    if (!cadencierStates || hasBuiltIndexes.current || indexes) return;

    setIsBuilding(true);
    setError(null);

    buildIndexes(searchData, cadencierStates)
      .then((result) => {
        setIndexes({
          ...result,
          societe_commerciale_id: searchData.societe_commerciale_id,
        });
        console.log('Search indexes built (France Frais products only).');
        setIsBuilding(false);
      })
      .catch((err: unknown) => {
        console.error('Failed to build search indexes:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsBuilding(false);
        hasBuiltIndexes.current = false;
      });

    hasBuiltIndexes.current = true;
  }, [cadencierStates, indexes, searchData]);

  useEffect(() => {
    async function insertMarketplaceItems() {
      if (
        !catalogueMarketplaceData ||
        !indexes ||
        hasInsertedMarketplace.current
      )
        return;

      const marketplaceItems = catalogueMarketplaceData.selection;
      if (marketplaceItems.length === 0) return;

      hasInsertedMarketplace.current = true;

      try {
        // Mappe les items marketplace vers le format HitItemCatalogue avant insertion
        const mappedItems = marketplaceItems.map(
          mapMarketplaceToHitItemCatalogue,
        );
        await insertMultiple(
          indexes.catalogue_idx,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          mappedItems as any,
          undefined,
          undefined,
          true,
        );
        console.log(
          `Inserted ${marketplaceItems.length} marketplace items into search index.`,
        );
        setMarketplaceReady(true);
      } catch (err: unknown) {
        console.error('Failed to insert marketplace items:', err);
        hasInsertedMarketplace.current = false;
      }
    }

    insertMarketplaceItems();
  }, [catalogueMarketplaceData, indexes]);

  const providerState = useMemo(
    () => ({
      error,
      isBuilding,
      marketplaceReady,
      indexVersion,
    }),
    [error, isBuilding, marketplaceReady, indexVersion],
  );

  return (
    <SearchClientProviderStateContext.Provider value={providerState}>
      <SearchClientContext.Provider value={indexes}>
        {children}
      </SearchClientContext.Provider>
    </SearchClientProviderStateContext.Provider>
  );
}

// Helper hook to use the provider state
export function useSearchClientProviderState() {
  return useContext(SearchClientProviderStateContext);
}
